import React from 'react';
import LegalPage, { LEGAL_BRAND, LEGAL_COMPANY, LEGAL_CONTACT_EMAIL } from './LegalPage';

// ── Terms & Conditions ────────────────────────────────────────────────────────
// Public at /terms. The messaging section (#sms) carries every disclosure the carriers' 10DLC registry requires of a
// program's terms (Telnyx "Campaign Compliance Requirements", read 2026-09-29) and must stay consistent with the
// registered campaign: brand 3AImigos; account notifications, customer care and 2FA; no marketing.
const EFFECTIVE = 'September 29, 2026';

export default function TermsPage() {
  return (
    <LegalPage title="Terms & Conditions" effective={EFFECTIVE} current="terms">
      <p>
        These Terms &amp; Conditions (“Terms”) govern your use of the {LEGAL_BRAND} platform and websites provided by{' '}
        {LEGAL_COMPANY} (“{LEGAL_BRAND}”, “we”, “us”), and your participation in the {LEGAL_BRAND} text-messaging program.
        By creating an account, using the platform, or opting in to text messages, you agree to these Terms and to our{' '}
        <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>1. The service</h2>
      <p>
        {LEGAL_BRAND} provides an AI-assisted business platform: project workspaces, document and content tools, and
        related features that may change over time. We may add, change or discontinue features, and we may suspend the
        service for maintenance or for security reasons.
      </p>

      <h2>2. Your account</h2>
      <ul>
        <li>You must provide accurate information and keep your credentials confidential. You are responsible for activity under your account.</li>
        <li>Tell us promptly if you believe your account has been accessed without authorization.</li>
        <li>You must be at least 18, or the age of majority where you live, to create an account.</li>
      </ul>

      <h2>3. Acceptable use</h2>
      <p>You agree not to use {LEGAL_BRAND} to:</p>
      <ul>
        <li>break the law, or infringe anyone’s rights, including intellectual-property and privacy rights;</li>
        <li>upload malicious code, or attempt to probe, disrupt or gain unauthorized access to the service or other users’ data;</li>
        <li>send unsolicited, deceptive or abusive communications through the service;</li>
        <li>resell or provide the service to third parties except as we expressly permit.</li>
      </ul>

      <h2>4. Your content and our platform</h2>
      <p>
        You keep ownership of the content you add to {LEGAL_BRAND}. You give us the permission needed to host, process
        and display it in order to provide the service to you, including passing it to the AI and infrastructure
        providers we use to operate the platform. The platform itself — its software, design and trademarks — belongs to{' '}
        {LEGAL_COMPANY} and its licensors. AI-generated output may be inaccurate; you are responsible for reviewing it
        before you rely on it.
      </p>

      <h2 id="sms">5. Text messaging (SMS / MMS) program</h2>
      <div className="legal-callout">
        <dl className="legal-kv">
          <dt>Program</dt><dd>{LEGAL_BRAND} ({LEGAL_COMPANY})</dd>
          <dt>Messages</dt><dd>Account notifications, customer-care communications, and authentication / two-factor (2FA) verification codes</dd>
          <dt>Frequency</dt><dd>Message frequency may vary</dd>
          <dt>Cost</dt><dd>Message and data rates may apply</dd>
          <dt>Opt out</dt><dd>Reply <strong>STOP</strong> or <strong>UNSUBSCRIBE</strong> at any time</dd>
          <dt>Help</dt><dd>Reply <strong>HELP</strong>, or email <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a></dd>
          <dt>Opt in again</dt><dd>Reply <strong>START</strong> or <strong>YES</strong></dd>
        </dl>
      </div>
      <h3>How you opt in</h3>
      <p>
        You join the program by providing your mobile phone number through a {LEGAL_BRAND} application and explicitly
        consenting to receive the applicable text messages. Consent to receive text messages is not a condition of any
        purchase. We send messages only to numbers whose owners have opted in.
      </p>
      <h3>What we send</h3>
      <p>
        The program is used for notifications about your account, for customer-care conversations you start or that
        relate to your account, and for verification codes that confirm it is you. We do not use this program for
        marketing or promotional messages.
      </p>
      <h3>Opting out and getting help</h3>
      <p>
        Reply <strong>STOP</strong> or <strong>UNSUBSCRIBE</strong> to any message and you will receive a single
        confirmation and no further messages from the program unless you opt in again by replying <strong>START</strong>{' '}
        or <strong>YES</strong>. Reply <strong>HELP</strong> for help at any time, or contact us at{' '}
        <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>. Opting out of text messages does not close
        your account; some account features that depend on verification codes may not be available by text after you opt out.
      </p>
      <h3>Carriers, cost and delivery</h3>
      <p>
        Message and data rates may apply according to your mobile plan; check with your carrier. Carriers are not liable
        for delayed or undelivered messages, and delivery is not guaranteed — messages may be delayed or fail because of
        carrier or network conditions outside our control. The program is intended for United States and Canadian
        mobile numbers.
      </p>
      <h3>Your responsibilities</h3>
      <ul>
        <li>Provide a mobile number that you own or are authorized to use, and keep it current.</li>
        <li>Tell us if you change or give up your mobile number so we do not message the next holder of that number.</li>
        <li>Do not use the program to send unlawful, abusive or unsolicited content.</li>
      </ul>
      <p>
        Your mobile information is handled as described in our <a href="/privacy#sms">Privacy Policy</a>; it will not be
        sold or shared with third parties for promotional or marketing purposes.
      </p>

      <h2>6. Fees</h2>
      <p>
        Where {LEGAL_BRAND} offers paid features, the price, billing period and cancellation terms are shown before you
        buy, and you authorize the corresponding charges. Taxes may apply.
      </p>

      <h2>7. Disclaimers</h2>
      <p>
        The service is provided “as is” and “as available”. To the extent permitted by law, we disclaim all warranties,
        express or implied, including merchantability, fitness for a particular purpose and non-infringement. We do not
        warrant that the service will be uninterrupted or error-free, or that AI-generated content will be accurate.
      </p>

      <h2>8. Limitation of liability</h2>
      <p>
        To the extent permitted by law, {LEGAL_COMPANY} will not be liable for indirect, incidental, special,
        consequential or punitive damages, or for lost profits, revenue or data, arising from your use of the service.
        Our total liability for any claim relating to the service is limited to the amount you paid us for the service in
        the twelve months before the claim, or one hundred US dollars if you have paid nothing. Nothing in these Terms
        limits liability that cannot be limited by law.
      </p>

      <h2>9. Termination</h2>
      <p>
        You may stop using the service or close your account at any time. We may suspend or terminate access if you
        breach these Terms or if required to protect the service or other users. Sections that by their nature should
        survive termination do so.
      </p>

      <h2>10. Changes to these Terms</h2>
      <p>
        We may update these Terms as the service changes. The effective date at the top shows when they were last
        revised; continued use after a change means you accept the revised Terms.
      </p>

      <h2>11. Contact</h2>
      <dl className="legal-kv">
        <dt>Company</dt><dd>{LEGAL_COMPANY}</dd>
        <dt>Brand</dt><dd>{LEGAL_BRAND}</dd>
        <dt>Email</dt><dd><a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a></dd>
        <dt>Website</dt><dd><a href="/">www.3aimigos.com</a></dd>
      </dl>
    </LegalPage>
  );
}
